import { fetchRequestWithToken } from '@/config/axios';
import { notify } from '@/helpers';
import { Note, noteStore } from '@/state/NoteStore';
import { useActivityLog } from '../useActivityLog';

export type NotePayload = {
  lead_id: string;
  note: string;
};

export const useNote = () => {
  const { getActivityLogs } = useActivityLog();

  const getNotes = (contactId: string | undefined) => {
    noteStore.setLoading(true);

    fetchRequestWithToken({
      url: `/contacts/get-lead-notes/${contactId}`,
      method: 'GET',
    })
      .then(response => {
        if (response?.data?.data && Array.isArray(response?.data?.data)) {
          noteStore.setNotes(response?.data?.data);
        }
      })
      .catch(error => {
        notify('error', error?.message);
      })
      .finally(() => {
        noteStore.setLoading(false);
      });
  };

  const deleteNote = (id: string, leadId?: string) => {
    noteStore.setLoading(true);
    fetchRequestWithToken({
      url: '/contacts/delete-lead-notes/' + id,
      method: 'DELETE',
    })
      .then(response => {
        if (response?.data?.data) {
          const copy = [...noteStore.notes];
          const index = copy.findIndex(item => {
            return item?._id === (response?.data?.data as Note)?._id;
          });
          copy.splice(index, 1);

          noteStore.setNotes(copy);
        }
      })
      .catch(error => {
        notify('error', error?.message);
      })
      .finally(() => {
        noteStore.setLoading(false);

        getActivityLogs(leadId, '', false);
      });
  };

  const createNote = (payload: NotePayload) => {
    noteStore.setLoading(true);

    fetchRequestWithToken({
      url: '/contacts/create-lead-note',
      method: 'POST',
      data: { lead_id: payload?.lead_id, note: payload?.note },
    })
      .then(response => {
        const copy = [...noteStore.notes];
        noteStore.setNotes([response?.data?.data as Note, ...copy]);
      })
      .catch(error => {
        notify('error', error?.message);
      })
      .finally(() => {
        noteStore.setLoading(false);

        getActivityLogs(payload?.lead_id, '', false);
      });
  };

  const editNote = (
    payload: Partial<NotePayload> & { id: string },
    leadId?: string,
  ) => {
    noteStore.setLoading(true);

    fetchRequestWithToken({
      url: `/contacts/update-lead-notes/${payload?.id}`,
      method: 'PUT',
      data: { note: payload?.note },
    })
      .then(response => {
        if (response?.data?.data) {
          const copy = [...noteStore.notes];
          const index = copy.findIndex(item => {
            return item?._id === (response?.data?.data as Note)?._id;
          });
          copy?.splice(index, 1, response?.data?.data as Note);

          noteStore.setNotes(copy);
        }
      })
      .catch(error => {
        notify('error', error.message);
      })
      .finally(() => {
        noteStore.setLoading(false);

        getActivityLogs(leadId, '', false);
      });
  };

  return {
    getNotes,
    editNote,
    createNote,
    deleteNote,
  };
};
